import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, formatError, imgUrl } from "@/lib/api";
import { PageTitle } from "./AdminApp";
import { ConfirmDelete } from "./RecordsTable";

const KINDS = [["logo", "Logo"], ["hero", "Hero"], ["gallery", "Gallery"], ["flyer", "Flyers"]];
const SINGLE = ["hero", "logo"];

const ImageCard = ({ img, i, total, onMove, onDelete, onCaption, kind }) => {
  const [caption, setCaption] = useState(img.caption || "");
  return (
    <div className="overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900" data-testid={`image-card-${img.id}`}>
      <img src={imgUrl(img.url)} alt={img.caption} className={`w-full ${kind === "logo" ? "aspect-video bg-[#fdf8ee] object-contain p-2" : kind === "flyer" ? "aspect-[3/4] object-cover" : "aspect-video object-cover"}`} />
      <div className="space-y-2 p-3">
        <Input value={caption} onChange={(e) => setCaption(e.target.value)} onBlur={() => caption !== img.caption && onCaption(img, caption)} placeholder="Caption" className="h-9 border-neutral-800 bg-neutral-950 text-sm" data-testid={`image-caption-${img.id}`} />
        <div className="flex items-center justify-between">
          {!SINGLE.includes(kind) ? (
            <div className="flex gap-1">
              <button disabled={i === 0} onClick={() => onMove(i, -1)} className="rounded p-1.5 text-neutral-400 hover:bg-neutral-800 disabled:opacity-30" data-testid={`image-move-up-${img.id}`}><ArrowUp className="h-4 w-4" /></button>
              <button disabled={i === total - 1} onClick={() => onMove(i, 1)} className="rounded p-1.5 text-neutral-400 hover:bg-neutral-800 disabled:opacity-30" data-testid={`image-move-down-${img.id}`}><ArrowDown className="h-4 w-4" /></button>
            </div>
          ) : <span className="text-xs text-neutral-500">Current {kind}</span>}
          <ConfirmDelete onConfirm={() => onDelete(img)} testid={`image-delete-${img.id}`} />
        </div>
      </div>
    </div>
  );
};

export default function ImagesManager() {
  const [kind, setKind] = useState("logo");
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/images").then((r) => setImages(r.data));
  useEffect(() => { load(); }, []);
  const list = images.filter((x) => x.kind === kind);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of SINGLE.includes(kind) ? files.slice(0, 1) : files) {
        const fd = new FormData();
        fd.append("kind", kind);
        fd.append("caption", "");
        fd.append("file", file);
        await api.post("/admin/images", fd);
      }
      toast.success("Uploaded");
      await load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onMove = async (i, dir) => {
    const next = [...list];
    [next[i], next[i + dir]] = [next[i + dir], next[i]];
    setImages([...images.filter((x) => x.kind !== kind), ...next]);
    try {
      await api.put("/admin/images/reorder", { kind, ids: next.map((x) => x.id) });
    } catch (err) {
      toast.error(formatError(err));
      load();
    }
  };
  const onDelete = async (img) => {
    try {
      await api.delete(`/admin/images/${img.id}`);
      toast.success("Image deleted");
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };
  const onCaption = async (img, caption) => {
    try {
      await api.patch(`/admin/images/${img.id}`, { caption });
      toast.success("Caption saved");
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  return (
    <div data-testid="admin-images">
      <PageTitle title="Images">
        <input ref={fileRef} type="file" accept="image/*" multiple={!SINGLE.includes(kind)} onChange={upload} className="hidden" data-testid="image-upload-input" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} className="flex h-10 items-center gap-2 rounded-full bg-amber-500 px-4 text-sm font-semibold text-neutral-950 hover:bg-amber-400 disabled:opacity-60" data-testid="image-upload-button">
          <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : SINGLE.includes(kind) ? `Replace ${kind}` : `Add to ${kind === "flyer" ? "flyers" : "gallery"}`}
        </button>
      </PageTitle>
      <div className="mb-6 inline-flex rounded-lg border border-neutral-800 bg-neutral-900 p-1">
        {KINDS.map(([k, l]) => (
          <button key={k} onClick={() => setKind(k)} data-testid={`images-tab-${k}`} className={`rounded-md px-4 py-2 font-display text-sm transition-colors ${kind === k ? "bg-amber-500 text-neutral-950" : "text-neutral-400 hover:text-neutral-100"}`}>
            {l} ({images.filter((x) => x.kind === k).length})
          </button>
        ))}
      </div>
      <div className={`grid gap-4 ${kind === "hero" ? "max-w-2xl" : kind === "logo" ? "max-w-xs" : "grid-cols-2 md:grid-cols-3 xl:grid-cols-4"}`}>
        {list.map((img, i) => (
          <ImageCard key={img.id} img={img} i={i} total={list.length} kind={kind} onMove={onMove} onDelete={onDelete} onCaption={onCaption} />
        ))}
      </div>
      {list.length === 0 && <p className="py-10 text-neutral-500" data-testid="images-empty">No images yet. Upload one above.</p>}
    </div>
  );
}
